<template>
    <div class="d-flex flex-column" @mousedown="emitFocus" @focusout="onLostFocus" @keyup.enter.prevent="emitFocus"
        @keydown.space.prevent="" @keyup.space.prevent="emitFocus">
        <slider v-model="localModel" :min="0" :selectable-min="0" :max="props.max" :step="step" :readonly="isReadOnly"
            @update:model-value="updateValue">
        </slider>
        <label ref="yearLabel" class="yearLabel border-rounded" :class="{ selected: isSelected }" data-bs-toggle="tooltip"
            data-bs-html="true" data-bs-placement="top" tabindex="0">
            <template v-if="showText">
                {{ labelText }}
            </template>
            <template v-else>
                <i class="bi bi-dot d-block dot"></i>
            </template>
        </label>
    </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { formatter } from "@/shared/ts/formatter";
import Slider from "../common/Slider.vue";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { getCalendarYearText } from "@/guiide/ts/wants/wantsUtilities";

const props = defineProps({
    modelValue: { type: Number, default: null },
    planYear: { type: Number, default: 1 },
    calendarYear: { type: Number, default: 2000 },
    startsOnJanFirst: { type: Boolean, default: false },
    max: { type: Number, default: 5000 },
    min: { type: Number, default: 0 },
    step: { type: Number, default: 50 },
    orientation: { type: String, default: "vertical" },
    showPlanYear: { type: Boolean, default: true },
    isSelected: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    showThumbLabel: { type: Boolean, default: true }
});

const labelText = computed<string>(() => {
    return props.planYear % 2 != 0 ? props.planYear.toString() : "";
});

const showText = computed<boolean>(() => {
    return labelText.value.length > 0;
})

const emit = defineEmits({
    'update:modelValue': null,
    'gotFocus': null
});

const localModel = ref(props.modelValue);
const hasFocus = ref(false);
const yearLabel = ref(null);
const bootstrap: any = (window as any).bootstrap;
let tooltip;

onMounted(() => {
    if (yearLabel.value) {
        createTooltip(getTooltipText.value);
    }
});

onBeforeUnmount(() => {
    if (tooltip.value) {
        tooltip.dispose();
    }
});

const updateValue = (newValue: number) => {
    emit('update:modelValue', newValue);
    if (!props.isSelected) {
        emit('gotFocus');
    }
};

watchEffect(() => {
    localModel.value = props.modelValue;
});

function emitFocus(event: Event) {
    hasFocus.value = true;
    emit("gotFocus");
}

function onLostFocus() {
    hasFocus.value = false;
}

const trackColor = computed(() => {
    return hasFocus.value ? "#bfbfbf" : "#DCDCDC";
})

const thumbColor = computed(() => {
    if (props.isReadOnly) {
        return "#A9A9A9";
    }

    if (localModel.value < props.min) {
        return "#B22222";
    }

    return hasFocus.value ? "#32CD32" : "green";
});

const selectedTrackColor = computed(() => {
    return localModel.value < props.min
        ? "red"
        : "#A9A9A9";
});

const tooltipCalendarYear = computed<string>(() => getCalendarYearText(props.calendarYear, props.startsOnJanFirst));

const getTooltipText = computed<string>(() => {
    return `${tooltipCalendarYear.value}<br/>`
        + `Annual: ${formatter.formatCurrency(localModel.value)}`;
});

watch(getTooltipText, (newText) => {
    if (yearLabel.value) {
        if (tooltip) {
            tooltip.hide();
            tooltip.dispose();
        }
        createTooltip(newText);
    }
});

function createTooltip(tooltipHtml: string) {
    tooltip = new bootstrap.Tooltip(yearLabel.value, {
        html: true,
        placement: 'top',
        title: tooltipHtml,
        delay: 600,
        trigger: 'hover'
    });
}

</script>

<style scoped>
.yearLabel {
    --hover-bg-color: rgba(205, 201, 201, 0.619);
    text-align: center;
    transition: background-color 0.3s ease, border 0.3s ease;
    cursor: pointer;
}

.yearLabel:hover {
    background-color: var(--hover-bg-color);
}

.yearLabel.selected {
    background-color: var(--hover-bg-color);
}

.yearLabel:focus {
    outline: none;
    background-color: rgba(235, 228, 228, 0.909);
}

.dot {
    font-size: 16px;
    color: rgb(128, 128, 128);
}
</style>
